<template>
  <v-dialog
    :value="true"
    max-width="700"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    persistent
  >
    <v-card class="pb-2">
      <v-card-title>
        <h2
          id="complete-signup-title"
          class="primary--text font-weight-bold line-height-lg"
        >
          {{ $t('wallet.complete-signup.title') }}
        </h2>
      </v-card-title>

      <v-card-subtitle class="pt-6 pb-2">
        <h4
          id="complete-signup-subtitle"
          class="primary--text font-weight-bold"
        >
          {{ $t('wallet.complete-signup.sub-title') }}
        </h4>
      </v-card-subtitle>

      <v-card-text>
        <h5
          id="complete-signup-message"
          class="primary--text line-height-lg"
        >
          {{ $t('wallet.complete-signup.message') }}
        </h5>

        <v-radio-group
          v-model="emailOption"
          @change="emailOptionChanged"
        >
          <v-radio
            :value="emailOptions.NEW"
            :label="$t('wallet.complete-signup.reward-email-options.new')"
          />

          <form-builder
            ref="formBuilder"
            :model="formData"
            :schema="formSchema"
            dense
            @field-changed="formFieldChanged"
          />

          <v-radio
            :value="emailOptions.EXISTING"
            :label="newEmailOptionLabel"
          />
        </v-radio-group>

        <v-btn
          id="complete-signup-submit-btn"
          block
          color="primary"
          large
          outlined
          class="text-capitalize mt-6 mb-2"
          :loading="isSubmitting"
          :disabled="isSubmitting"
          @click="submit"
        >
          {{ $t('wallet.complete-signup.submit-button-label') }}
        </v-btn>
      </v-card-text>

      <v-card-actions class="justify-center">
        <router-link
          id="complete-signup-no-email-link"
          :to="didNotReceiveViaEmailRoute"
        >
          {{ $t('wallet.complete-signup.no-email-link-label') }}
        </router-link>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { EmailAddressField } from '@/components/shared/forms/WalletCommonFormFields'
import { FormBuilder } from '@tangocard/mango'
import { WALLET_DASHBOARD } from '@/router/routes/route-names'
import { WALLET_EMAIL_TYPES } from '@/store/constants/wallet-constants'
import { GET_RECIPIENT_DETAILS } from '@/store/get-types'
import { FETCH_RECIPIENT_DETAILS } from '@/store/action-types'
import { WalletApi } from '@/cowboy/wallet/WalletApi'

export const emailOptions = Object.freeze({
  NEW: 'NEW_EMAIL',
  EXISTING: 'EXISTING'
})

export default {
  name: 'CompleteSignupForm',
  components: {
    FormBuilder
  },
  data: () => ({
    emailOption: emailOptions.EXISTING,
    formData: {
      emailAddress: ''
    },
    isSubmitting: false
  }),
  computed: {
    ...mapGetters({
      recipientDetails: GET_RECIPIENT_DETAILS
    }),
    didNotReceiveViaEmailRoute: () => ({ path: '/' }),
    emailAddressFieldDisabled: () => ({
      ...EmailAddressField,
      props: {
        ...EmailAddressField.props,
        disabled: true
      }
    }),
    emailOptions: () => emailOptions,
    formSchema () {
      const fields = this.isNewEmailOptionSelected
        ? [EmailAddressField]
        : [this.emailAddressFieldDisabled]
      return { fields }
    },
    isNewEmailOptionSelected () {
      return this.emailOption === emailOptions.NEW
    },
    newEmailOptionLabel () {
      return this.$t('wallet.complete-signup.reward-email-options.existing', {
        email: this.primaryEmail
      })
    },
    primaryEmail () {
      const { emails } = this.recipientDetails || {}
      const recipientEmails = emails || []
      const primaryEmail = recipientEmails.find(
        ({ type }) => type === WALLET_EMAIL_TYPES.PRIMARY
      )
      const { emailAddress } = primaryEmail || {}
      return emailAddress
    }
  },
  methods: {
    ...mapActions({
      fetchRecipientDetails: FETCH_RECIPIENT_DETAILS
    }),
    emailOptionChanged () {
      if (!this.isNewEmailOptionSelected) {
        this.$refs.formBuilder.resetValidation()
      }
    },
    formFieldChanged (field, value) {
      this.formData[field] = value
    },
    async submit () {
      if (this.isNewEmailOptionSelected) {
        const isValid = this.$refs.formBuilder.validate()
        if (!isValid) return
      }

      this.isSubmitting = true
      const rewardsEmail = this.isNewEmailOptionSelected
        ? this.formData.emailAddress
        : null

      await WalletApi.completeSignup(rewardsEmail)
      await this.fetchRecipientDetails()
      await this.$router.push({ name: WALLET_DASHBOARD })
      this.isSubmitting = false
    }
  }
}
</script>

<style lang="scss" scoped>
.line-height-lg {
  line-height: 1.5;
}
</style>
